import { useEffect, useState } from 'react';
import {
  MIN_PLACES_QUERY_LENGTH,
  googlePlacesService,
  type PlacePrediction,
} from '../../services/googlePlacesService';

export type AddressSearchState =
  | { status: 'idle' }
  /** Keeps the last results on screen while the next request is in flight,
   *  so the list does not blink on every keystroke. */
  | { status: 'searching'; predictions: PlacePrediction[] }
  | { status: 'done'; predictions: PlacePrediction[] }
  | { status: 'error'; error: Error };

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Debounced Places autocomplete for `query`, limited to `countryCode` when
 * one is given. A reply that lands after the query has moved on is dropped,
 * so a slow early request can never overwrite a later one.
 */
export function useAddressSearch(query: string, countryCode: string | null): AddressSearchState {
  const [state, setState] = useState<AddressSearchState>({ status: 'idle' });

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_PLACES_QUERY_LENGTH) {
      setState({ status: 'idle' });
      return undefined;
    }

    let isCurrent = true;
    setState((previous) => ({
      status: 'searching',
      predictions: previous.status === 'done' || previous.status === 'searching'
        ? previous.predictions
        : [],
    }));

    const timer = setTimeout(() => {
      googlePlacesService.getAutocompletePredictions(trimmed, countryCode)
        .then((predictions) => {
          if (isCurrent) setState({ status: 'done', predictions });
        })
        .catch((err: Error) => {
          console.error('Address search failed:', err.message);
          if (isCurrent) setState({ status: 'error', error: err });
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
  }, [countryCode, query]);

  return state;
}
